import Cookies from 'universal-cookie';

import { getAwsRum } from './initRum';
import { getRumFeatureKey, getRumPageKey, getRumRouteContext, RUM_SURFACE_SCOPE } from './rumPageId';

const cookies = new Cookies();

const RUM_ROLE_STORAGE_KEY = 'rum_user_role';
const RUM_ACTOR_TYPE_STORAGE_KEY = 'rum_actor_type';
const RUM_LOGIN_FLOW_STORAGE_KEY = 'rum_login_flow';
const SUPPORT_ACCESS_LEVELS = new Set(['s1', 's2', 's3']);

function readLocalStorage(key) {
  if (typeof window === 'undefined') return '';
  return localStorage.getItem(key) || '';
}

function writeLocalStorage(key, value) {
  if (typeof window === 'undefined') return;
  localStorage.setItem(key, value);
}

function removeLocalStorage(key) {
  if (typeof window === 'undefined') return;
  localStorage.removeItem(key);
}

function sanitizeString(value) {
  return String(value ?? '')
    .trim()
    .slice(0, 256);
}

function sanitizeIdentifier(value) {
  return sanitizeString(value)
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
    .replace(/[^A-Za-z0-9._-]+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_+|_+$/g, '')
    .toLowerCase();
}

function getCurrentUserEmail() {
  return (
    readLocalStorage('user_email') ||
    readLocalStorage('email') ||
    cookies.get('user_email') ||
    cookies.get('email') ||
    ''
  );
}

function getCurrentAccountId() {
  return cookies.get('accountId') || readLocalStorage('accountId') || '';
}

function getCurrentInsightUserId() {
  return readLocalStorage('UserId') || readLocalStorage('userId') || cookies.get('userId') || '';
}

function inferActorType() {
  const storedActorType = readLocalStorage(RUM_ACTOR_TYPE_STORAGE_KEY);
  if (storedActorType) return storedActorType;

  const accessLevel = readLocalStorage('accessLevel') || '';
  if (SUPPORT_ACCESS_LEVELS.has(accessLevel) || cookies.get('userSubRole')) {
    return 'support_agent';
  }

  return getCurrentUserEmail() || getCurrentAccountId() ? 'customer' : '';
}

function cleanEventPayload(payload) {
  return Object.entries(payload || {}).reduce((acc, [key, value]) => {
    if (value === undefined || value === null) return acc;
    if (typeof value === 'string') {
      const sanitizedValue = sanitizeString(value);
      if (!sanitizedValue) return acc;
      acc[key] = sanitizedValue;
      return acc;
    }
    if (typeof value === 'number' || typeof value === 'boolean') {
      acc[key] = value;
    }
    return acc;
  }, {});
}

function recordRumEvent(eventType, payload) {
  const client = getAwsRum();
  if (!client) return;

  const eventData = cleanEventPayload(payload);
  if (!Object.keys(eventData).length) return;

  try {
    client.recordEvent(eventType, eventData);
  } catch (_) {
    /* ignore */
  }
}

export function persistRumLoginFlow(loginFlow) {
  writeLocalStorage(RUM_LOGIN_FLOW_STORAGE_KEY, sanitizeIdentifier(loginFlow));
}

export function consumeRumLoginFlow(defaultFlow = 'direct') {
  const loginFlow = readLocalStorage(RUM_LOGIN_FLOW_STORAGE_KEY) || defaultFlow;
  removeLocalStorage(RUM_LOGIN_FLOW_STORAGE_KEY);
  return sanitizeIdentifier(loginFlow) || defaultFlow;
}

export function setRumSessionIdentity({
  accountId,
  userEmail,
  insightUserId,
  userRole,
  actorType,
  surfaceScope = RUM_SURFACE_SCOPE
}) {
  const client = getAwsRum();
  const resolvedActorType = sanitizeIdentifier(actorType) || inferActorType();
  const attributes = {
    account_id: sanitizeString(accountId),
    user_email: sanitizeString(userEmail),
    insight_user_id: sanitizeString(insightUserId),
    user_role: sanitizeIdentifier(userRole),
    surface_scope: sanitizeIdentifier(surfaceScope) || RUM_SURFACE_SCOPE,
    actor_type: resolvedActorType
  };

  writeLocalStorage(RUM_ROLE_STORAGE_KEY, attributes.user_role || '');
  writeLocalStorage(RUM_ACTOR_TYPE_STORAGE_KEY, resolvedActorType || '');

  if (!client) return;

  try {
    client.addSessionAttributes(attributes);
  } catch (_) {
    /* ignore */
  }
}

export function syncRumSessionIdentityFromStorage() {
  const accountId = getCurrentAccountId();
  const userEmail = getCurrentUserEmail();
  const insightUserId = getCurrentInsightUserId();
  const userRole = readLocalStorage(RUM_ROLE_STORAGE_KEY) || '';
  const actorType = inferActorType();

  if (!accountId && !userEmail && !insightUserId && !userRole && !actorType) {
    return;
  }

  setRumSessionIdentity({
    accountId,
    userEmail,
    insightUserId,
    userRole,
    actorType
  });
}

export function clearRumSessionIdentity() {
  removeLocalStorage(RUM_ROLE_STORAGE_KEY);
  removeLocalStorage(RUM_ACTOR_TYPE_STORAGE_KEY);
  removeLocalStorage(RUM_LOGIN_FLOW_STORAGE_KEY);

  const client = getAwsRum();
  if (!client) return;

  try {
    client.addSessionAttributes({
      account_id: '',
      user_email: '',
      insight_user_id: '',
      user_role: '',
      surface_scope: RUM_SURFACE_SCOPE,
      actor_type: ''
    });
  } catch (_) {
    /* ignore */
  }
}

function getCurrentPageContext() {
  const pathname = typeof window !== 'undefined' ? window.location.pathname || '/' : '/';
  return getRumRouteContext(pathname);
}

export function getRumNavScope(itemId, targetPath) {
  const normalizedItemId = sanitizeString(itemId);

  if (normalizedItemId.endsWith('-mspLevel')) return 'msp';
  if (normalizedItemId.endsWith('-parOrg')) return 'organization';
  if (normalizedItemId.endsWith('-loc')) return 'location';
  if (normalizedItemId.endsWith('-customgroup')) return 'device_group';

  return getRumRouteContext(targetPath).scope;
}

export function getRumNavId(itemId, targetPath) {
  const normalizedItemId = sanitizeString(itemId);
  if (normalizedItemId) {
    const parts = normalizedItemId.split('-');
    return sanitizeIdentifier(parts.slice(0, -1).join('-') || normalizedItemId);
  }

  return sanitizeIdentifier(getRumFeatureKey(targetPath));
}

export function recordRumUserLogin({
  userEmail,
  insightUserId,
  effectiveAccountId,
  loginFlow,
  pageKey
}) {
  recordRumEvent('insight.user_login', {
    user_email: userEmail || getCurrentUserEmail(),
    insight_user_id: insightUserId || getCurrentInsightUserId(),
    effective_account_id: effectiveAccountId || getCurrentAccountId(),
    login_flow: sanitizeIdentifier(loginFlow) || 'direct',
    page_key: pageKey || getRumPageKey(typeof window !== 'undefined' ? window.location.pathname || '/' : '/')
  });
}

export function recordRumNavClick({
  navScope,
  navId,
  featureKey,
  targetRouteTemplate,
  effectiveAccountId,
  userEmail
}) {
  recordRumEvent('insight.nav_click', {
    user_email: userEmail || getCurrentUserEmail(),
    effective_account_id: effectiveAccountId || getCurrentAccountId(),
    nav_scope: sanitizeIdentifier(navScope),
    nav_id: sanitizeIdentifier(navId),
    feature_key: sanitizeString(featureKey),
    target_route_template: sanitizeString(targetRouteTemplate)
  });
}

export function recordRumCtaClick({
  featureKey,
  pageKey,
  actionId,
  componentType,
  effectiveAccountId,
  userEmail
}) {
  recordRumEvent('insight.cta_click', {
    user_email: userEmail || getCurrentUserEmail(),
    effective_account_id: effectiveAccountId || getCurrentAccountId(),
    feature_key: featureKey || getCurrentPageContext().featureKey,
    page_key: pageKey || getCurrentPageContext().pageId,
    action_id: sanitizeIdentifier(actionId),
    component_type: sanitizeIdentifier(componentType) || 'button'
  });
}

export function getRumPageKeyFromWindow() {
  return getRumPageKey(typeof window !== 'undefined' ? window.location.pathname || '/' : '/');
}

export function getRumFeatureKeyFromWindow() {
  return getRumFeatureKey(typeof window !== 'undefined' ? window.location.pathname || '/' : '/');
}

export function recordCartoTileUsageBatch({ mapSurface, tileCount, monthlyBrowserTotal }) {
  recordRumEvent('insight.carto_tile_usage', {
    map_surface: sanitizeIdentifier(mapSurface) || 'unknown',
    tile_count: Number(tileCount) || 0,
    monthly_browser_total: Number(monthlyBrowserTotal) || 0
  });
}

export { sanitizeIdentifier };
